import { apiFetch } from './client';
import type {
  BackfillState,
  BackfillStatus,
  EmailAccount,
  EmailAccountTestResult,
  EmailReviewGroup,
  Me,
  MeTrackedDomain
} from '$lib/types';

export const fetchMe = () => apiFetch<Me>('/me');

// The logged-in user's self-service email connections — a user may connect
// more than one mailbox (see backend GET/POST /api/me/email-accounts +
// PUT/DELETE/test/sync by :id, internal/api/handlers/email_account.go).
export const meEmailAccountApi = {
  list: () => apiFetch<{ results: EmailAccount[] }>('/me/email-accounts'),
  create: (body: Record<string, unknown>) =>
    apiFetch<EmailAccount>('/me/email-accounts', { method: 'POST', body: JSON.stringify(body) }),
  save: (id: number, body: Record<string, unknown>) =>
    apiFetch<EmailAccount>(`/me/email-accounts/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => apiFetch<{ detail: string }>(`/me/email-accounts/${id}`, { method: 'DELETE' }),
  // body is optional: pass in-progress form values to test before saving, or
  // omit to re-test the already-saved account.
  test: (id: number, body?: Record<string, unknown>) =>
    apiFetch<EmailAccountTestResult>(`/me/email-accounts/${id}/test`, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined
    }),
  // Trigger an immediate sync of one connected mailbox (bypassing the
  // ~5-minute ticker). ran=false means a sync was already in progress.
  syncNow: (id: number) =>
    apiFetch<{ ran: boolean; account?: EmailAccount; detail?: string }>(`/me/email-accounts/${id}/sync`, {
      method: 'POST'
    })
};

// The unmatched-email review queue + retroactive backfill for the logged-in
// user's own mailbox (see internal/api/handlers/email_review.go). All 403
// for a user whose email_access has been revoked — same gate as
// meEmailAccountApi's GET/PUT/test/sync.
export const meEmailReviewApi = {
  list: () => apiFetch<{ groups: EmailReviewGroup[] }>('/me/email-review'),
  // counterparty is a full address or "@domain" — same normalization as
  // meEmailBackfillApi.start.
  ignore: (counterparty: string) =>
    apiFetch<{ detail: string }>('/me/email-review/ignore', {
      method: 'POST',
      body: JSON.stringify({ counterparty })
    }),
  // Batch form of the above — server caps at 100 per call (maxIgnoreBatch in
  // email_review.go); callers with more than that must chunk.
  ignoreBatch: (counterparties: string[]) =>
    apiFetch<{ detail: string; count: number }>('/me/email-review/ignore', {
      method: 'POST',
      body: JSON.stringify({ counterparties })
    }),
  // Flags an address/domain as one of our own people — org-wide, and clears
  // it from every account's review queue (internal_addresses.go). Any
  // email-access user may call it, not just admins.
  markInternal: (counterparty: string) =>
    apiFetch<{ detail: string }>('/me/email-review/internal', {
      method: 'POST',
      body: JSON.stringify({ counterparty })
    })
};

// Read-only "domains tracked in your vertical" list (see
// handlers.meListTrackedDomains) — own accessible verticals only, behind the
// same RequireEmailAccess gate as the rest of this file.
export const meTrackedDomainsApi = {
  list: () => apiFetch<{ results: MeTrackedDomain[] }>('/me/tracked-domains')
};

export const meEmailBackfillApi = {
  // 202'd immediately — the server queues it per-account (never 409s
  // anymore); poll status() for progress. counterparty must already resolve
  // to a CRM person or tracked domain (400 otherwise — add the person /
  // track the domain first).
  start: (counterparty: string) =>
    apiFetch<{ detail: string; status: BackfillState; position: number }>('/me/email-backfill', {
      method: 'POST',
      body: JSON.stringify({ counterparty })
    }),
  status: () => apiFetch<BackfillStatus>('/me/email-backfill/status')
};
